import { Infobox, TextInputBasic } from '@zoom/components';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Popover } from 'reactstrap';

// eslint-disable-next-line import-x/no-cycle
import { DropdownTimezoneButton, DropdownTimezoneContext } from '.';
import { formatTimezoneIdToName } from '../../utils/timezoneFormatter';

type Props = {
  items: string[];
  minHeightThreshold?: number;
  showSearch?: boolean;
} & React.PropsWithChildren;

export const DropdownTimezoneSubmenu: React.FC<Props> = ({
  children,
  items,
  minHeightThreshold,
  showSearch = false,
}) => {
  const context = useContext(DropdownTimezoneContext);

  if (!context) {
    throw new Error('DropdownTimezoneSubmenu must be used inside DropdownTimezone.');
  }

  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const [isContainerAttached, setContainerAttached] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setContainerAttached(true);
  }, []);

  const { activeTimezone, alignment } = context;

  const filteredItems = items.filter((item) =>
    formatTimezoneIdToName(item).toLowerCase().includes(searchTerm.toLowerCase().trim())
  );

  const close = () => setIsOpen(false); // Workaround for popper not disappearing after leaving submenu
  const toggle = () => setIsOpen((open) => !open);

  return (
    <div className="dropdown-timezone__submenu-container" ref={containerRef}>
      {children}

      {isContainerAttached && (
        <Popover
          isOpen={isOpen}
          trigger="hover"
          target={containerRef.current!}
          container={containerRef.current!}
          popperClassName="dropdown-timezone__submenu-popper"
          placement={alignment === 'right' ? 'right-start' : 'left-start'}
          fade={false}
          hideArrow
          offset="0,-8px"
          toggle={toggle}
          modifiers={{
            setMaxHeight: {
              enabled: true,
              order: 890,
              fn: (data) => {
                // use `containerRef` as main point of reference to compute available height, popper as fallback
                const referenceElement = containerRef.current ? containerRef.current : (data.instance as any).popper;
                const referenceClientRect = referenceElement.getBoundingClientRect();

                let availableHeight = window.innerHeight - referenceClientRect.y;
                if (minHeightThreshold === undefined || availableHeight > minHeightThreshold) {
                  // Space from the viewport bottom
                  availableHeight -= 50;
                }

                return {
                  ...data,
                  styles: {
                    ...data.styles,
                    '--dropdown-timezone__submenu--height': `${availableHeight}px`,
                  },
                };
              },
            },
          }}
        >
          <div className="dropdown-timezone__submenu" onMouseLeave={close}>
            {showSearch && (
              <div className="px-4 py-3">
                <TextInputBasic
                  input={{
                    name: 'search',
                    onChange: (e) => setSearchTerm(e.target.value),
                    value: searchTerm,
                  }}
                  state={undefined}
                  size="100"
                />
              </div>
            )}

            {filteredItems.length === 0 && <Infobox>{t('filter.noResults')}</Infobox>}

            <PerfectScrollbar>
              <div>
                {filteredItems.map((item) => (
                  <DropdownTimezoneButton key={item} active={item === activeTimezone} tz={item}>
                    {formatTimezoneIdToName(item)}
                  </DropdownTimezoneButton>
                ))}
              </div>
            </PerfectScrollbar>
          </div>
        </Popover>
      )}
    </div>
  );
};
